// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type React from 'react'

/**
 * D-PERF-2 — the LIST scrolls, not the page.
 *
 * Patrick, after approving this on the prescriptions queue: "push it to all
 * screens — Patients, Secure share, prescribers, support, and sub screens like
 * my active prescriptions, history."
 *
 * ONE definition, imported everywhere, deliberately. The courier-token
 * expression was copy-pasted into ten places this week and had already drifted
 * into two spellings before anyone noticed; a scroll offset repeated across
 * twenty screens would drift exactly the same way, and the symptom — one screen
 * scrolling differently from the rest — is the kind of thing that gets reported
 * as a bug months later. If the offset needs tuning it is tuned here, once.
 */

/**
 * Vertical scroll lives INSIDE the list container so the toolbar, tabs and
 * column headers stay put while rows move under them.
 *
 * 100dvh, never 100vh: iOS Safari's vh includes the collapsing browser chrome,
 * so vh makes the container taller than the visible area and re-introduces the
 * page scrollbar this exists to remove — on the device this is tested on.
 *
 * `chromeAbove` is the space above the list on that screen (header, tabs,
 * filters). It varies per screen, which is why it is a parameter rather than a
 * constant — a single hardcoded offset is what would make one screen wrong.
 */
export const listScrollStyle = (chromeAbove = 320): React.CSSProperties => ({
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: `calc(100dvh - ${chromeAbove}px)`,
  width: '100%',
  WebkitOverflowScrolling: 'touch',
  // Stops a scroll that reaches the end of the list from continuing into the
  // page behind it.
  overscrollBehavior: 'contain',
})

/**
 * Column headers stay visible for every row — the point of moving the scrollbar
 * inside. Spread this AFTER the screen's own header style so its background is
 * preserved: the header must be opaque or rows scroll visibly beneath it, and a
 * second `background` key here would both override the theme and, if written
 * inline, be a duplicate-key compile error (which is exactly how the first
 * version of this failed CI).
 */
export const stickyHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
}

/**
 * WHY STICKY HEADERS SILENTLY DID NOTHING THE FIRST TIME.
 *
 * `position: sticky` on a `<th>` is IGNORED by Chrome and Safari when the table
 * uses `border-collapse: collapse` — no warning, no error, the header simply
 * scrolls away. Every list here used collapse, so the sticky rule I shipped was
 * inert on all of them.
 *
 * The fix is `border-collapse: separate` with `border-spacing: 0`, which renders
 * identically for these tables (borders are declared per-cell, not shared) while
 * allowing sticky to apply. Spread this into a table's style wherever
 * stickyHeaderStyle is used on its headers — the two belong together, and this
 * comment exists so the next person does not "tidy up" the separate back to
 * collapse and quietly break it again.
 */
export const stickyTableStyle: React.CSSProperties = {
  borderCollapse: 'separate',
  borderSpacing: 0,
}
